import {CommentsAPI} from 'api/comments';
import {TrackingAPI} from 'api/tracking';
import {Button} from 'components/system/button';
import {Select} from 'components/system/select';
import {Sticky} from 'components/system/sticky';
import {useConfirmation} from 'hooks/confirmation';
import {useIsVisible} from 'hooks/is_visible';
import {useLocalStorageState} from 'hooks/local_storage_state';
import {useEffect, useMemo, useRef, useState} from 'react';
import {DeletedCommentBody} from 'types/comments';
import {Upsell, useAuth, useUpsell} from 'users';
import {classNames} from 'utils';
import {
  addNested,
  applyCommentListConfig,
  defaultCommentListConfig,
  orderingOptions,
  replaceNested,
} from 'utils/comment_list';
import {Comment} from './comment';
import {CommentBluredPlaceholder} from './commentBluredPlaceholder';
import {CommentEditor} from './comment_editor';
import styles from './comment_list.module.css';

export const CommentList = ({
  comments: initialComments,
  contentType,
  contentIdentifier,
  newCommentPlaceholder,
  upsellBarrier = false,
  onAddComment,
}: Props) => {
  const {loginRequired} = useUpsell();
  const {user} = useAuth();
  const [comments, setComments] = useState<UserComment[]>(initialComments);
  const [config, setConfig] = useState(defaultCommentListConfig);
  const ref = useRef(null);
  const isVisible = useIsVisible(ref);

  const [cachedValue, setCachedValue] = useLocalStorageState(
    `comment-${contentType}-${contentIdentifier}`,
    '',
  );
  const {setShouldWarn} = useConfirmation(
    false,
    "You haven't submitted your comment, are you sure you want to leave the page?",
  );

  useEffect(() => {
    if (isVisible)
      TrackingAPI.commentSection({
        action: 'view',
        content_type: contentType,
        content_identifier: contentIdentifier,
      });
  }, [isVisible]);
  const visibleComments = useMemo(() => {
    if (initialComments.length !== 0 || !upsellBarrier) {
      return applyCommentListConfig(comments, config);
    }
    return [];
  }, [comments, config]);

  useEffect(() => {
    setComments(initialComments);
  }, [initialComments]);

  const handleCreate = (path: number[], newComment: UserComment) => {
    newComment.new_comment = true;
    setComments(addNested(comments, path, newComment));
    if (onAddComment) onAddComment();
  };

  const handleChange = (path: number[], updatedComment: UserComment) => {
    setComments(replaceNested(comments, path, updatedComment));
  };

  const handleDelete = (path: number[], deletedComment: UserComment) => {
    setComments(replaceNested(comments, path, {...deletedComment, body: DeletedCommentBody}));
  };

  const allowToWriteComment = loginRequired({
    sourceObject: {contentType, uuid: contentIdentifier},
    cta: 'question.comment',
  });

  return (
    <div className={styles.container}>
      {user.loggedIn && (
        <CommentEditor
          submitLabel='Submit comment'
          placeholder={newCommentPlaceholder}
          initialValue={cachedValue}
          allowClick={allowToWriteComment}
          onChange={(value) => {
            setShouldWarn(value !== '');
            setCachedValue(value);
          }}
          onSubmit={(value) => CommentsAPI.createComment(value, contentType, contentIdentifier)}
          onSuccess={(comment) => {
            handleCreate([], comment);
            TrackingAPI.commentSection({
              action: 'submit',
              content_type: contentType,
              content_identifier: contentIdentifier,
              extra_data: {
                value: comment.body_text,
                comment_type: 'new',
              },
            });
          }}
        />
      )}
      {!upsellBarrier ? (
        <div ref={ref}>
          {visibleComments.length === 0 && (
            <div className={styles.empty}>
              There are no comments yet. Start the conversation by leaving a comment.
            </div>
          )}
          {visibleComments.length > 0 && (
            <div className={classNames([styles.toolbar, styles.hasBorder])}>
              <Select
                size='sm'
                placeholder='Sort by'
                options={orderingOptions}
                value={config.ordering}
                onChange={(ordering) => setConfig({...config, ordering})}
              />
            </div>
          )}
          {visibleComments.map((comment) => (
            <Comment
              key={comment.id}
              comment={comment}
              onChange={handleChange}
              onDelete={handleDelete}
              onCreate={handleCreate}
            />
          ))}
          {visibleComments.length > 5 && (
            <div className={styles.toolbar}>
              <Button
                type='outline'
                size='sm'
                onClick={() => {
                  window.scrollTo({top: 0, behavior: 'smooth'});
                }}>
                Back to top
              </Button>
            </div>
          )}
        </div>
      ) : (
        <>
          <Sticky>
            <div className={styles.upsellContainer}>
              <div>
                <Upsell
                  sourceObject={{contentType, uuid: contentIdentifier}}
                  cta='question.comment'
                />
              </div>
            </div>
          </Sticky>
          <CommentBluredPlaceholder />
        </>
      )}
    </div>
  );
};

type Props = {
  comments: UserComment[];
  contentType: CommentContentType;
  contentIdentifier: string;
  newCommentPlaceholder: string;
  upsellBarrier?: boolean;
  onAddComment?: () => void;
};
